import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { cdiApi } from "@/services/api";
import {
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface CdiRate {
  id: string;
  referenceDate: string;
  rate: string;
  createdAt: string;
  updatedAt: string;
}

interface SchedulerStatus {
  nextRun: string;
  schedule: string;
  timezone: string;
  description: string;
}

export function CdiSettingsPage() {
  const [rates, setRates] = useState<CdiRate[]>([]);
  const [currentRate, setCurrentRate] = useState<CdiRate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRate, setSelectedRate] = useState<CdiRate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    referenceDate: "",
    rate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Result feedback
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [ratesData, current, status] = await Promise.all([
        cdiApi.list(30),
        cdiApi.getCurrent().catch(() => null),
        cdiApi.getSchedulerStatus().catch(() => null),
      ]);
      setRates(ratesData);
      setCurrentRate(current);
      setSchedulerStatus(status);
    } catch (error) {
      console.error("Erro ao carregar taxas CDI:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setResult(null);
    try {
      const response = await cdiApi.sync();
      setResult({
        success: true,
        message: response.message,
      });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao sincronizar CDI",
      });
    } finally {
      setSyncing(false);
    }
  }

  function resetForm() {
    setFormData({ referenceDate: "", rate: "" });
    setErrors({});
  }

  function openAddModal() {
    resetForm();
    // Default to today's date
    const today = new Date().toISOString().split("T")[0];
    setFormData({ referenceDate: today, rate: "" });
    setShowAddModal(true);
  }

  function openEditModal(rate: CdiRate) {
    setSelectedRate(rate);
    setFormData({
      referenceDate: rate.referenceDate.split("T")[0],
      rate: rate.rate,
    });
    setErrors({});
    setShowEditModal(true);
  }

  function openDeleteConfirm(rate: CdiRate) {
    setSelectedRate(rate);
    setConfirmDelete(true);
  }

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!formData.referenceDate) {
      newErrors.referenceDate = "Data de referência é obrigatória";
    }

    if (!formData.rate) {
      newErrors.rate = "Taxa é obrigatória";
    } else {
      const rateNum = parseFloat(formData.rate);
      if (isNaN(rateNum) || rateNum <= 0) {
        newErrors.rate = "Taxa deve ser um número positivo";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleAdd() {
    if (!validateForm()) return;

    setSaving(true);
    setResult(null);
    try {
      await cdiApi.create({
        referenceDate: formData.referenceDate,
        rate: parseFloat(formData.rate),
      });
      setShowAddModal(false);
      resetForm();
      setResult({ success: true, message: "Taxa CDI adicionada com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao adicionar taxa",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!validateForm() || !selectedRate) return;

    setSaving(true);
    setResult(null);
    try {
      await cdiApi.update(selectedRate.id, {
        rate: parseFloat(formData.rate),
      });
      setShowEditModal(false);
      resetForm();
      setSelectedRate(null);
      setResult({ success: true, message: "Taxa CDI atualizada com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao atualizar taxa",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selectedRate) return;

    setDeleting(true);
    try {
      await cdiApi.delete(selectedRate.id);
      setConfirmDelete(false);
      setSelectedRate(null);
      setResult({ success: true, message: "Taxa CDI removida com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao remover taxa",
      });
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Configuração de CDI"
          description="Taxa CDI diária para cálculo de rendimentos"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Configuração de CDI"
        description="Taxa CDI diária para cálculo de rendimentos"
        stats={[
          { value: rates.length, label: "Registros" },
          {
            value: currentRate ? `${parseFloat(currentRate.rate).toFixed(4)}%` : "—",
            label: "CDI Atual",
          },
        ]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSync} disabled={syncing}>
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sincronizar BC
            </Button>
            <Button onClick={openAddModal}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Taxa
            </Button>
          </div>
        }
      />

      {/* Current CDI Card */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">CDI Atual</p>
                  <p className="text-2xl font-bold">
                    {currentRate
                      ? `${parseFloat(currentRate.rate).toFixed(4)}% a.d.`
                      : "Não configurado"}
                  </p>
                </div>
              </div>
              <Badge variant={currentRate ? "success" : "secondary"}>
                {currentRate ? "Ativo" : "Pendente"}
              </Badge>
            </div>
            {currentRate && (
              <p className="mt-3 text-sm text-muted-foreground">
                Data de referência: {formatDate(currentRate.referenceDate)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Scheduler Status Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">Atualização Automática</p>
                  <p className="text-sm text-muted-foreground">
                    {schedulerStatus?.description || "Carregando..."}
                  </p>
                </div>
              </div>
              <Badge variant="success">Ativo</Badge>
            </div>
            {schedulerStatus && (
              <p className="mt-3 text-sm text-muted-foreground">
                Próxima execução: {formatDate(schedulerStatus.nextRun)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Result feedback */}
      {result && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            result.success
              ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {result.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{result.message}</span>
        </div>
      )}

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico de Taxas</CardTitle>
        </CardHeader>
        <CardContent>
          {rates.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma taxa cadastrada ainda.
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">
                      Data de Referência
                    </th>
                    <th className="text-left p-4 font-medium text-sm">Taxa (a.d.)</th>
                    <th className="text-left p-4 font-medium text-sm">Atualizado em</th>
                    <th className="text-right p-4 font-medium text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rates.map((rate) => (
                    <tr key={rate.id} className="hover:bg-muted/50">
                      <td className="p-4 text-sm">
                        {formatDate(rate.referenceDate)}
                      </td>
                      <td className="p-4 text-sm font-medium">
                        {parseFloat(rate.rate).toFixed(6)}%
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDate(rate.updatedAt)}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(rate)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteConfirm(rate)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Nova Taxa CDI</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Data de Referência</label>
                <Input
                  type="date"
                  value={formData.referenceDate}
                  onChange={(e) =>
                    setFormData({ ...formData, referenceDate: e.target.value })
                  }
                  error={errors.referenceDate}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Taxa Diária (%)</label>
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="0.043123"
                  value={formData.rate}
                  onChange={(e) =>
                    setFormData({ ...formData, rate: e.target.value })
                  }
                  error={errors.rate}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Informe a taxa diária do CDI (ex: 0.043123 para 0.043123% a.d.)
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleAdd} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Editar Taxa CDI</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Data de Referência</label>
                <Input
                  type="date"
                  value={formData.referenceDate}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A data de referência não pode ser alterada
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Taxa Diária (%)</label>
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="0.043123"
                  value={formData.rate}
                  onChange={(e) =>
                    setFormData({ ...formData, rate: e.target.value })
                  }
                  error={errors.rate}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedRate(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setSelectedRate(null);
        }}
        onConfirm={handleDelete}
        title="Excluir Taxa CDI"
        description={`Tem certeza que deseja excluir a taxa de ${
          selectedRate ? formatDate(selectedRate.referenceDate) : ""
        }? Esta ação não pode ser desfeita.`}
        variant="danger"
        loading={deleting}
      />
    </div>
  );
}
